import React, { useState } from 'react';
import { FaPencilAlt, FaTrashAlt } from 'react-icons/fa';
import {
  Button,
  Card,
  Col,
  Container,
  Row,
  Modal,
  Alert,
} from 'react-bootstrap';
import MetaTags from 'react-meta-tags';
import DataLoader from '../loaders/DataLoader';
import { isNotNull } from '../validators/validators';
import { convertYYYYMMDDToDDMMYYYY } from '../dateFormates/dateFormats';

/**
 * Common TravellerDetails Component
 * Reusable component for managing traveller/passenger information
 *
 * @param {Object} props - Component props
 * @param {string} props.companyName - Company name for meta tags
 * @param {string} props.pageTitle - Page title
 * @param {string} props.pageDescription - Page description
 * @param {Array} props.passengersData - Array of passenger data
 * @param {Function} props.onFetchPassengers - Callback to fetch passengers
 * @param {Function} props.onDeletePassenger - Callback to delete passenger (receives rowData, returns Promise)
 * @param {boolean} props.dataAvailability - Whether data is available
 * @param {boolean} props.noData - Whether there's no data
 * @param {React.Component} props.AddPassengerComponent - Component for adding passenger
 * @param {React.Component} props.EditPassengerComponent - Component for editing passenger
 * @param {React.Component} props.SideNavComponent - Side navigation component
 * @param {Object} props.userData - User data object
 * @param {Function} props.userInfo - Callback to update user info
 */
function TravellerDetails({
  companyName,
  pageTitle = 'Traveller Details',
  pageDescription = 'Add, update, or delete passenger information',
  passengersData = [],
  onFetchPassengers,
  onDeletePassenger,
  dataAvailability = false,
  noData = false,
  AddPassengerComponent,
  EditPassengerComponent,
  SideNavComponent,
  userData = null,
  userInfo,
}) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPassenger, setSelectedPassenger] = useState(null);
  const [alertShow, setAlertShow] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertVariant, setAlertVariant] = useState('success');
  const [isDeleting, setIsDeleting] = useState(false);

  // Handle showing add modal
  const handleShowAddModal = () => {
    setShowAddModal(true);
  };

  // Handle closing add modal
  const handleCloseAddModal = (message, variant) => {
    setShowAddModal(false);
    if (message) {
      setAlertMessage(message);
      setAlertVariant(variant || 'success');
      setAlertShow(true);
      setTimeout(() => {
        setAlertShow(false);
      }, 5000);
    }
  };

  // Handle editing passenger
  const handleEdit = (passenger) => {
    setSelectedPassenger(passenger);
    setShowEditModal(true);
  };

  // Handle closing edit modal
  const handleCloseEditModal = (success, message, variant) => {
    setShowEditModal(false);
    setSelectedPassenger(null);
    if (success !== undefined) {
      if (success && message) {
        setAlertMessage(message);
        setAlertVariant(variant || 'success');
        setAlertShow(true);
        setTimeout(() => {
          setAlertShow(false);
        }, 5000);
      }
    }
  };

  // Handle deleting passenger
  const handleDelete = async (passenger) => {
    if (!window.confirm('Are you sure you want to delete this passenger?')) {
      return;
    }

    setIsDeleting(true);
    try {
      const result = await onDeletePassenger(passenger);
      if (result && result.status === 'success') {
        setAlertMessage(
          result.payload?.message || 'Passenger deleted successfully'
        );
        setAlertVariant('success');
        setAlertShow(true);
        setTimeout(() => {
          setAlertShow(false);
        }, 5000);

        // Refresh passenger list
        if (onFetchPassengers) {
          onFetchPassengers();
        }
      } else {
        setAlertMessage(result?.error?.message || 'Failed to delete passenger');
        setAlertVariant('danger');
        setAlertShow(true);
        setTimeout(() => {
          setAlertShow(false);
        }, 5000);
      }
    } catch (error) {
      console.error('Delete passenger error:', error);
      setAlertMessage('An error occurred while deleting the passenger');
      setAlertVariant('danger');
      setAlertShow(true);
      setTimeout(() => {
        setAlertShow(false);
      }, 5000);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <MetaTags>
        <title>
          {pageTitle} | {companyName}
        </title>
        <meta name="description" content={pageDescription} />
      </MetaTags>

      <Container className="pageContainer cont-top maContainer">
        {/* Alert for success/error messages */}
        {alertShow && (
          <Alert
            variant={alertVariant}
            dismissible
            onClose={() => setAlertShow(false)}
            className="mt-3"
          >
            {alertMessage}
          </Alert>
        )}

        <Row>
          {/* Side Navigation */}
          {SideNavComponent && (
            <Col md="3" className="prifilenav">
              <SideNavComponent pageName="P_D" userData={userData} />
            </Col>
          )}

          {/* Main Content */}
          <Col md={SideNavComponent ? '9' : '12'} className="profilebody">
            <Card>
              <Card.Header className="pfHeaderArea">
                <div className="myAccHeader d-flex flex-column">
                  <h2>{pageTitle}</h2>
                  <span className="mt-1">{pageDescription}</span>
                </div>
                {AddPassengerComponent && (
                  <Button
                    onClick={handleShowAddModal}
                    size="sm"
                    variant="btn btn-primary"
                    disabled={isDeleting}
                  >
                    Add Traveller
                  </Button>
                )}
              </Card.Header>
              <Card.Body>
                <div className="passengers-info">
                  {dataAvailability ? (
                    passengersData && passengersData.length > 0 ? (
                      passengersData.map((pass, i) => (
                        <div
                          className="passenger-data-list"
                          key={pass.psdid || i}
                        >
                          <div className="pasengermaininfo">
                            <Row className="pasengerinfo">
                              <Col className="passnamedata">
                                <strong>
                                  {isNotNull(pass.fsnm) && isNotNull(pass.lsnm)
                                    ? `${pass.fsnm} ${pass.lsnm}`
                                    : userData?.em || 'N/A'}
                                </strong>
                              </Col>
                            </Row>
                            <Row className="pasengerinfo">
                              <Col md="8" className="pass-details">
                                <div className="pass-data">
                                  {(pass.tle || pass.typ) && (
                                    <small>
                                      {pass.typ && pass.typ !== null
                                        ? `${pass.typ} - `
                                        : ''}
                                      {pass.tle &&
                                        (pass.tle === 'M'
                                          ? 'Male'
                                          : pass.tle === 'F'
                                          ? 'Female'
                                          : '')}
                                    </small>
                                  )}
                                  <span>
                                    DOB:{' '}
                                    {isNotNull(pass.dob)
                                      ? convertYYYYMMDDToDDMMYYYY(pass.dob)
                                      : '-'}
                                  </span>
                                </div>
                              </Col>
                              <Col md="4" className="editarea">
                                {EditPassengerComponent && (
                                  <Button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => handleEdit(pass)}
                                    disabled={isDeleting}
                                  >
                                    <FaPencilAlt />
                                  </Button>
                                )}
                                {pass.lpx !== 1 && onDeletePassenger && (
                                  <Button
                                    className="btn btn-outline-danger btn-sm ms-2"
                                    onClick={() => handleDelete(pass)}
                                    disabled={isDeleting}
                                  >
                                    <FaTrashAlt />
                                  </Button>
                                )}
                              </Col>
                            </Row>
                          </div>
                          <Row className="additionalinfo">
                            <Col md="6" className="additional-details">
                              <strong>Passport No</strong>
                              <span>
                                {isNotNull(pass.pspno) ? pass.pspno : '-'}
                              </span>
                            </Col>
                            <Col md="6" className="additional-details">
                              <strong>Nationality</strong>
                              <span>
                                {isNotNull(pass.ntly) ? pass.ntly : '-'}
                              </span>
                            </Col>
                          </Row>
                        </div>
                      ))
                    ) : (
                      <DataLoader noData={true} />
                    )
                  ) : (
                    <DataLoader noData={noData} />
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Add Passenger Modal */}
      {AddPassengerComponent && (
        <Modal
          show={showAddModal}
          onHide={() => handleCloseAddModal()}
          size={'xl'}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Add Traveller</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <AddPassengerComponent
              close={handleCloseAddModal}
              onFetchPassengers={onFetchPassengers}
            />
          </Modal.Body>
        </Modal>
      )}

      {/* Edit Passenger Modal */}
      {EditPassengerComponent && selectedPassenger && (
        <Modal
          show={showEditModal}
          onHide={() => handleCloseEditModal()}
          size={'xl'}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Edit Traveller</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <EditPassengerComponent
              rowData={selectedPassenger}
              close={handleCloseEditModal}
              onFetchPassengers={onFetchPassengers}
              userData={userInfo || (() => {})}
            />
          </Modal.Body>
        </Modal>
      )}
    </>
  );
}

export default TravellerDetails;
